// ═══════════════════════════════════════════════════════════════
// MAPEO SECCIONES → TIPOS DE TAREA
// ═══════════════════════════════════════════════════════════════
//
// Documento: configuracion/mapeoSeccionesTareas
//
// Estructura:
//   { "publicidad__activar": "tipoTareaIdXxx", ... }
//
// Clave: `${area}__${seccion}` (doble guión bajo)
// Valor: ID del tipo de tarea del catálogo
//
// Se lee una vez al cargar la app y se cachea en memoria.
// El admin puede editar las relaciones desde la UI (barra flotante).
// ═══════════════════════════════════════════════════════════════

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase'

const DOC_PATH = 'configuracion/mapeoSeccionesTareas'

// ── Cache en memoria ──
let _cache = null
let _loading = null // Promise en vuelo para evitar lecturas duplicadas

/**
 * Carga el mapeo (con cache). Fuerza re-lectura con force=true.
 * @returns {Object} mapa { "area__seccion": tipoTareaId }
 */
export async function cargarMapeo(force = false) {
  if (_cache && !force) return _cache
  if (_loading) return _loading

  _loading = (async () => {
    try {
      const snap = await getDoc(doc(db, DOC_PATH))
      const data = snap.exists() ? snap.data() : {}
      // Filtrar campos internos de Firestore
      const mapeo = {}
      for (const [k, v] of Object.entries(data)) {
        if (k !== 'actualizadoEn' && k !== 'actualizadoPor' && typeof v === 'string') {
          mapeo[k] = v
        }
      }
      _cache = mapeo
      return mapeo
    } catch (e) {
      console.warn('[mapeoTareas] Error cargando mapeo:', e)
      _cache = {}
      return {}
    } finally {
      _loading = null
    }
  })()

  return _loading
}

/**
 * Obtiene el tipoTareaId para una sección de un dashboard.
 * @param {string} area - ej: 'publicidad'
 * @param {string} seccion - ej: 'activar'
 * @returns {string|null} tipoTareaId o null si no hay mapeo
 */
export async function obtenerTipoTarea(area, seccion) {
  const mapeo = await cargarMapeo()
  const clave = `${area}__${seccion}`
  return mapeo[clave] || null
}

/**
 * Establece o actualiza la relación sección → tipo de tarea.
 * @param {string} area
 * @param {string} seccion
 * @param {string} tipoTareaId
 * @param {string} userId - quién hizo el cambio
 */
export async function asignarTipoTarea(area, seccion, tipoTareaId, userId) {
  const clave = `${area}__${seccion}`
  await setDoc(doc(db, DOC_PATH), {
    [clave]: tipoTareaId,
    actualizadoEn: serverTimestamp(),
    actualizadoPor: userId,
  }, { merge: true })
  // Actualizar cache
  if (_cache) _cache[clave] = tipoTareaId
}

/**
 * Elimina la relación sección → tipo de tarea.
 * @param {string} area
 * @param {string} seccion
 * @param {string} userId
 */
export async function desasignarTipoTarea(area, seccion, userId) {
  const clave = `${area}__${seccion}`
  const { deleteField } = await import('firebase/firestore')
  await setDoc(doc(db, DOC_PATH), {
    [clave]: deleteField(),
    actualizadoEn: serverTimestamp(),
    actualizadoPor: userId,
  }, { merge: true })
  // Actualizar cache
  if (_cache) delete _cache[clave]
}

/**
 * Devuelve todo el mapeo (para la UI de admin).
 */
export async function listarMapeos() {
  return cargarMapeo(true)
}

// ── Uso por tipo de tarea (jul 2026) ────────────────────────────
// Invierte el mapa para responder "¿en qué dashboard/sección se usa
// cada tipo?": { tipoTareaId: [{ area, seccion, clave }] }.
// Lo consumen la tabla de Tipos de Tarea (SLAsPage) y el desplegable
// del engranaje de AccionableBar (🟢 en uso / 🔴 sin uso).

/** Labels legibles por área (fallback: el slug tal cual). */
export const AREA_LABELS = {
  publicidad: 'Publicidad',
  preguntas: 'Preguntas y Respuestas',
  opiniones: 'Opiniones',
  postventa: 'Postventa',
  'velocidad-consumo': 'Velocidad de consumo',
  rentabilidad: 'Rentabilidad por orden',
  stock: 'Stock',
  pyr: 'P&R (legacy)',
}

/**
 * @param {boolean} force - re-leer de Firestore (para la UI de admin)
 * @returns {Object} { tipoTareaId: [{ area, seccion, clave }] }
 */
export async function listarMapeosPorTipo(force = false) {
  const mapeo = await cargarMapeo(force)
  const porTipo = {}
  for (const [clave, tipoId] of Object.entries(mapeo)) {
    const idx = clave.indexOf('__')
    if (idx === -1) continue
    const area = clave.slice(0, idx)
    const seccion = clave.slice(idx + 2)
    if (!porTipo[tipoId]) porTipo[tipoId] = []
    porTipo[tipoId].push({ area, seccion, clave })
  }
  return porTipo
}

// ── Exponer en window (admin) ──
if (typeof window !== 'undefined') {
  window.__mapeoTareas = {
    cargarMapeo,
    obtenerTipoTarea,
    asignarTipoTarea,
    desasignarTipoTarea,
    listarMapeos,
  }
}
